import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Package, MapPin, Edit2, Plus, Trash2, Star, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const TABS = [
  { key: 'profile', label: 'My Profile', icon: User },
  { key: 'orders', label: 'My Orders', icon: Package },
  { key: 'addresses', label: 'Addresses', icon: MapPin },
];

const EMPTY_ADDR = { first_name: '', last_name: '', full_address: '', door_no: '', city: '', state: '', pincode: '', mobile: '', address_type: 'Home', latitude: null, longitude: null, is_default: false };

export default function ProfilePage() {
  const { user, fetchProfile } = useAuth();
  const [params, setParams] = useSearchParams();
  const active = params.get('tab') || 'profile';
  const setActive = (t) => setParams({ tab: t });

  return (
    <div data-testid="profile-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl sm:text-4xl font-serif font-medium mb-8">My Account</h1>
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar */}
        <aside data-testid="profile-sidebar" className="space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key}
                data-testid={`profile-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`w-full text-left px-4 py-3 rounded-2xl flex items-center gap-3 text-sm font-medium transition-colors ${active === t.key ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/50 text-muted-foreground'}`}>
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </aside>

        {/* Content */}
        <div>
          {active === 'profile' && <ProfileSection user={user} onUpdated={fetchProfile} />}
          {active === 'orders' && <OrdersSection />}
          {active === 'addresses' && <AddressesSection />}
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ user, onUpdated }) {
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.verified_phone || user?.phone || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || '');
    setPhone(user?.verified_phone || user?.phone || '');
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/auth/update-profile', { name, phone });
      toast.success('Profile updated');
      if (onUpdated) await onUpdated();
    } catch {
      toast.error('Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-section" className="bg-card rounded-3xl border border-border/50 p-6 sm:p-8">
      <div className="flex items-center gap-5 mb-6">
        <div className="relative">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
              <span className="text-2xl font-semibold text-primary-foreground">{(user?.name || 'U')[0].toUpperCase()}</span>
            </div>
          )}
          <button className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-background shadow-md border flex items-center justify-center hover:bg-secondary transition-colors">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <div>
          <h2 className="text-xl font-serif font-medium">{user?.name || 'Customer'}</h2>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      <div className="space-y-4 max-w-md">
        <div>
          <Label>Full Name</Label>
          <Input data-testid="profile-name-input" value={name} onChange={e => setName(e.target.value)} className="mt-1.5 rounded-2xl" />
        </div>
        <div>
          <Label>Email</Label>
          <Input value={user?.email || ''} disabled className="mt-1.5 rounded-2xl bg-muted/50" />
        </div>
        <div>
          <Label className="flex items-center justify-between">
            <span>Mobile Number</span>
            {user?.verified_phone && <span className="text-[10px] text-primary flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Verified</span>}
          </Label>
          <Input data-testid="profile-phone-input" value={phone} onChange={e => setPhone(e.target.value)} className="mt-1.5 rounded-2xl" />
        </div>
        <Button data-testid="profile-update-btn" onClick={save} disabled={saving} className="rounded-full px-8">
          {saving ? 'Updating...' : 'Update'}
        </Button>
      </div>
    </div>
  );
}

function OrdersSection() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/orders').then(r => { setOrders(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (orders.length === 0) {
    return (
      <div data-testid="no-orders" className="bg-card rounded-3xl border border-border/50 p-10 text-center">
        <Package className="h-14 w-14 mx-auto text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground">You haven't placed any orders yet.</p>
      </div>
    );
  }
  const statusClass = {
    pending: 'bg-yellow-100 text-yellow-800',
    pending_cash_verification: 'bg-amber-100 text-amber-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-blue-100 text-blue-800',
    packed: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return (
    <div data-testid="orders-section" className="space-y-3">
      {orders.map((o) => (
        <div key={o.order_id} data-testid={`order-${o.order_id}`} className="bg-card rounded-3xl border border-border/50 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-semibold text-sm">{o.order_id}</p>
              <p className="text-[11px] text-muted-foreground">{new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <Badge className={`${statusClass[o.status] || 'bg-muted'} border-0 rounded-full text-xs capitalize`}>{(o.status || '').replace(/_/g, ' ')}</Badge>
          </div>
          <div className="space-y-1.5">
            {(o.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded-xl object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{it.product_name}</p>
                  <p className="text-[11px] text-muted-foreground">{it.variant_name} x{it.quantity}</p>
                </div>
                <span className="text-sm font-medium">{'\u20B9'}{it.price * it.quantity}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
            <span className="text-xs text-muted-foreground">Total</span>
            <span className="text-base font-semibold">{'\u20B9'}{o.total}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddressesSection() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = no modal; object = editing / new

  const refresh = () => api.get('/addresses').then(r => { setAddresses(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    await api.delete(`/addresses/${id}`);
    toast.success('Deleted');
    refresh();
  };
  const setDefault = async (id) => {
    await api.put(`/addresses/${id}/default`);
    toast.success('Default address updated');
    refresh();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="addresses-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-serif font-medium">Saved Addresses</h2>
        <Button data-testid="add-address-btn" onClick={() => setEditing({ ...EMPTY_ADDR })} className="rounded-full gap-2" size="sm">
          <Plus className="h-3.5 w-3.5" /> Add New
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="bg-card rounded-3xl border border-border/50 p-10 text-center">
          <MapPin className="h-14 w-14 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No saved addresses yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} data-testid={`address-${a.id}`} className="bg-card rounded-3xl border border-border/50 p-5 relative">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="rounded-full text-[10px]">{a.address_type}</Badge>
                  {a.is_default && <Badge className="rounded-full text-[10px] bg-primary/10 text-primary border-primary/20">Default</Badge>}
                </div>
              </div>
              <p className="font-medium text-sm">{a.first_name} {a.last_name}</p>
              <p className="text-sm text-muted-foreground mt-1">{a.door_no}, {a.full_address}</p>
              <p className="text-sm text-muted-foreground">{a.city} {a.state} {a.pincode}</p>
              <p className="text-sm text-muted-foreground mt-1">{a.mobile}</p>
              <div className="flex gap-2 mt-4">
                {!a.is_default && <button onClick={() => setDefault(a.id)} className="text-xs text-primary hover:underline">Set as Default</button>}
                <button data-testid={`edit-address-${a.id}`} onClick={() => setEditing(a)} className="text-xs text-primary hover:underline">Edit</button>
                <button data-testid={`delete-address-${a.id}`} onClick={() => remove(a.id)} className="text-xs text-destructive hover:underline ml-auto flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddressDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        initial={editing}
        onSaved={() => { setEditing(null); refresh(); }}
      />
    </div>
  );
}

function AddressDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_ADDR);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (initial) setForm({ ...EMPTY_ADDR, ...initial });
  }, [initial]);

  if (!open) return null;

  const capture = () => {
    if (!navigator.geolocation) return;
    setCapturing(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      setForm(f => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`)
        .then(r => r.json()).then(g => {
          const a = g.address || {};
          setForm(f => ({ ...f, full_address: f.full_address || g.display_name || '', city: f.city || a.city || a.town || a.village || '', state: f.state || a.state || '', pincode: f.pincode || a.postcode || '' }));
        }).catch(() => {});
      setCapturing(false);
      toast.success('Location captured');
    }, () => { toast.error('Could not get location'); setCapturing(false); });
  };

  const save = async () => {
    if (!form.first_name || !form.mobile || !form.full_address || !form.pincode) {
      toast.error('Fill required fields'); return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/addresses/${form.id}`, form);
        toast.success('Address updated');
      } else {
        await api.post('/addresses', form);
        toast.success('Address added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="address-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.id ? 'Edit Address' : 'Add New Address'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input data-testid="addr-first-name" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} /></div>
            <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} /></div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Address *</Label>
              <Button type="button" variant="ghost" size="sm" onClick={capture} disabled={capturing} className="h-6 text-xs text-primary">
                {capturing ? 'Locating...' : 'Use Current Location'}
              </Button>
            </div>
            <Input data-testid="addr-full" value={form.full_address} onChange={e => setForm({...form, full_address: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Door / Flat No *</Label><Input data-testid="addr-door" value={form.door_no} onChange={e => setForm({...form, door_no: e.target.value})} /></div>
            <div><Label>Pincode *</Label><Input data-testid="addr-pincode" value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value})} /></div>
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
            <div><Label>State</Label><Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} /></div>
            <div className="col-span-2"><Label>Mobile *</Label><Input data-testid="addr-mobile" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} /></div>
          </div>
          <div>
            <Label>Address Type</Label>
            <div className="flex gap-2 mt-1">
              {['Home','Work','Other'].map(t => (
                <button key={t} type="button" onClick={() => setForm({...form, address_type: t})}
                  className={`px-4 py-1.5 rounded-full text-xs border ${form.address_type === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>{t}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_default} onChange={e => setForm({...form, is_default: e.target.checked})} className="h-4 w-4" />
            Set as default
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="save-address-btn" onClick={save} disabled={saving} className="rounded-full">{saving ? 'Saving...' : 'Save Address'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
